import type en from '../en/common'
import type { Translation } from '../types'

/** Words and formats used across the dashboard */
const common: Translation<typeof en> = {
  "common.justNow": "gerade eben",
  "common.minutesAgo": "vor {count} Min.",
  "common.hoursAgo": "vor {count} Std.",
  "common.daysAgo": "vor {count} T.",
  "common.duration.minutes": { one: "{count} Minute", other: "{count} Minuten" },
  "common.duration.hours": { one: "{count} Stunde", other: "{count} Stunden" },
  "common.duration.days": { one: "{count} Tag", other: "{count} Tage" },
  "common.duration.weeks": { one: "{count} Woche", other: "{count} Wochen" },
  "common.cancel": "Abbrechen",
  "common.confirm": "Bestätigen",
  "common.continue": "Weiter",
  "common.working": "Wird ausgeführt",
  "common.loading": "Wird geladen…",
  "common.tryAgain": "Das hat nicht funktioniert – bitte versuchen Sie es erneut",
  "common.tryAgainButton": "Erneut versuchen",
  "common.retry": "Wiederholen",
  "common.copy": "Kopieren",
  "common.download": "Herunterladen",
  "common.saved": "Gespeichert",
  "common.notSaved": "Nicht gespeichert",
  "common.couldNotCopy": "Kopieren nicht möglich",
  "common.goBack": "Zurück",
  "common.password": "Passwort",
  "common.emailAddress": "E-Mail-Adresse",
  "common.showPassword": "Passwort anzeigen",
  "common.hidePassword": "Passwort verbergen",
  "common.signOut": "Abmelden",
  "common.notFoundTitle": "Seite nicht gefunden",
  "common.notFoundText": "Die gesuchte Seite existiert nicht oder Sie haben keinen Zugriff darauf.",
  "common.errorTitle": "Etwas ist schiefgelaufen",
  "common.errorUnexpected": "Ein unerwarteter Fehler ist aufgetreten.",
  "common.loadFailed": "Die Daten konnten nicht geladen werden.",
  "common.showing": "{start}–{end} von {total}",
  "common.previousPage": "Vorherige Seite",
  "common.nextPage": "Nächste Seite",
  "common.notifications": "Benachrichtigungen",
  "common.dismiss": "Schließen",
  "common.refresh": "Aktualisieren",
  "common.viewAll": "Alle anzeigen",
  "common.view": "Anzeigen",
  "common.delete": "Löschen",
  "common.close": "Schließen",
  "common.actions": "Aktionen",
  "common.status": "Status",
  "common.name": "Name",
  "common.description": "Beschreibung",
  "common.clients": "Clients",
  "common.devices": "Geräte",
  "common.active": "Aktiv",
  "common.disabled": "Deaktiviert",
  "common.saveFailed": "Speichern fehlgeschlagen",
  "common.internalError": "Interner Serverfehler",
  "common.clientsCount": { one: "{count} Client", other: "{count} Clients" },
  "common.devicesCount": { one: "{count} Gerät", other: "{count} Geräte" },
  "common.onlineCount": "{count} online",
  "common.critical": { one: "{count} kritisch", other: "{count} kritisch" },
  "common.status.ONLINE": "ONLINE",
  "common.status.OFFLINE": "OFFLINE",
  "common.status.WARNING": "WARNUNG",
  "common.status.ERROR": "FEHLER",
  "common.status.PENDING": "AUSSTEHEND",
  "common.status.PENDING_VPN": "WARTET AUF VPN",
  "common.status.PROVISIONING": "WIRD EINGERICHTET",
  "common.status.ACTIVE": "AKTIV",
  "common.status.INACTIVE": "INAKTIV",
  "common.status.COMPLETED": "ABGESCHLOSSEN",
  "common.status.RUNNING": "LÄUFT",
  "common.status.FAILED": "FEHLGESCHLAGEN",
  "common.status.EXPIRED": "ABGELAUFEN",
  "common.status.SUSPENDED": "GESPERRT",
  "common.status.DELETED": "GELÖSCHT",
  "common.status.FAILOVER": "FAILOVER",
  "common.status.APPLIED": "ÜBERNOMMEN",
  "common.status.UNSUPPORTED": "NICHT UNTERSTÜTZT",
  "common.select": "Auswählen…",
  "common.selectAll": "Alle auswählen",
  "common.deselectAll": "Alle abwählen",
  "common.more": "+{count} weitere",
  "common.optional": "(optional)",
  "common.sitesCount": { one: "{count} Standort", other: "{count} Standorte" },
  "common.send": "Senden",
  "common.uptime.days": "{d} T. {h} Std.",
  "common.uptime.hours": "{h} Std. {m} Min.",
  "common.uptime.minutes": "{m} Min.",
  "common.copied": "Kopiert",
  "common.remove": "Entfernen",
  "common.saving": "Wird gespeichert…",
  "common.username": "Benutzername",
  "common.tryAgainMoment": "Versuchen Sie es gleich noch einmal.",
  "common.duration.seconds": { one: "{count} Sekunde", other: "{count} Sekunden" },
  "common.status.PENDING_SETUP": "IN EINRICHTUNG",
  "common.status.CANCELLED": "GEKÜNDIGT",
  "common.languageName.en": "Englisch",
  "common.languageName.nl": "Niederländisch",
  "common.languageName.fr": "Französisch",
  "common.languageName.de": "Deutsch",
  "common.languageName.es": "Spanisch",
  "common.status.QUEUED": "FÜR SPÄTER GESPEICHERT",
  "common.status.DISCARDED": "VERWORFEN",
  "common.renewCert.title": "Das VPN-Zertifikat von {name} erneuern?",
  "common.renewCert.titleMine": "Ihr VPN-Zertifikat erneuern?",
  "common.renewCert.stops": "Das aktuelle Zertifikat funktioniert sofort nicht mehr: bis die neue Datei installiert ist, gibt es keine VPN-Verbindung.",
  "common.renewCert.remote": "Ein Gerät oder Controller, das nur über das VPN erreichbar ist, ist dann von hier aus nicht mehr erreichbar: für die neue Datei kann jemand vor Ort nötig sein.",
  "common.renewCert.noWayBack": "Das lässt sich nicht rückgängig machen: das alte Zertifikat kann nicht wieder verwendet werden.",
  "common.renewCert.continue": "Verstanden, weiter",
  "common.renewCert.last": "Das Zertifikat jetzt erneuern? Das aktuelle funktioniert nicht mehr, sobald Sie klicken.",
  "common.renewCert.renewNow": "Jetzt erneuern",
  "common.export.button": "Exportieren",
  "common.export.title": "Exportieren",
  "common.export.what": "Inhalt",
  "common.export.summaryNone": "Alles Angezeigte",
  "common.export.rows": "Zeilen",
  "common.export.thisPage": "Diese Seite ({n})",
  "common.export.allMatches": "Alle Treffer ({n})",
  "common.export.tooMany": "Mehr als 10 000 Zeilen: Exportieren Sie diese Seite oder grenzen Sie die Filter ein",
  "common.export.parts": "Teile",
  "common.export.format": "Format",
  "common.export.xlsx": "Tabelle (.xlsx)",
  "common.export.csv": "Tabelle (CSV)",
  "common.export.pdf": "PDF-Bericht",
  "common.export.md": "Markdown-Auftrag für Claude Code",
  "common.export.go": "Exportieren",
  "common.export.working": "Wird exportiert…",
  "common.export.failed": "Der Export hat nicht geklappt",
  "common.export.search": "Suche „{q}“",
  "common.export.sortedBy": "sortiert nach {column}",
  "common.export.sortedByDesc": "sortiert nach {column}, absteigend",
  "common.export.field": "Feld",
  "common.export.value": "Wert",
  "common.export.deviceInfo": "Geräteinfo",
  "common.export.siteInfo": "Standortinfo",
  "common.export.traffic": "Verkehr",
  "common.export.cpu": "CPU",
  "common.export.serial": "Seriennummer",
  "common.export.mac": "MAC-Adresse",
  "common.export.level": "Stufe",
  "common.export.title_": "Titel",
  "common.export.message": "Meldung",
  "common.export.time": "Zeit",
  "common.export.security": "Sicherheit",
  "common.export.band": "Band",
  "common.export.vlan": "VLAN",
  "common.export.ssid": "SSID",
  "common.export.enabled": "Aktiviert",
  "common.export.clientCount": "Clients",
  "common.export.period": "Zeitraum {period}",
  "common.export.range": "{from} bis {to}",
  "common.export.radios": "Funkmodule",
  "common.export.modem": "Modem",
  "common.export.operator": "Netzbetreiber",
  "common.export.network": "Netztyp",
  "common.export.events": "Ereignisse",
  "common.export.controller": "Controller",
  "common.export.wired": "Kabel",
  "common.export.eventInfo": "Veranstaltungsinfo",
  "common.export.online": "Online und offline",
  "common.export.loads": "Raus und zurück",
  "common.export.customer": "Kunde",
  "common.export.resolvedAt": "Behoben",
  "common.export.loadedAt": "Geladen",
  "common.export.loadedBy": "Geladen von",
  "common.export.returnedAt": "Zurückgekommen",
  "common.export.returnedBy": "Zurückgebracht von",
  "common.export.figures": "Kennzahlen",
  "common.export.critical": "Kritisch",
  "common.export.vlans": "VLANs",
  "common.export.wifi": "WLAN",
  "common.export.kind": "Art",
  "common.export.changedBy": "Geändert von",
  "common.export.changedAt": "Geändert am",
  "common.export.change": "Änderung",
  "common.export.articleInfo": "Artikelangaben",
  "common.export.when": "Wann",
  "common.export.copied": "Kopiert und heruntergeladen",
  "common.export.downloadedOnly": "Heruntergeladen (Kopieren ist hier nicht erlaubt)",
  "common.export.twoFactor": "Zwei-Faktor-Anmeldung",
  "common.export.systemRole": "Systemrolle",
  "common.export.type": "Typ",
  "common.export.deviceFamily": "Gerätefamilie",
  "common.export.origin": "Herkunft",
  "common.export.isDefault": "Standard",
  "common.export.madeBy": "Angelegt von",
  "common.export.created": "Erstellt",
  "common.export.applied": "Mal angewendet",
  "common.export.watches": "Überwacht",
  "common.export.channels": "Kanäle",
  "common.export.mutedDevices": "Stummgeschaltete Geräte",
  "common.export.who": "Wer",
  "common.export.action": "Aktion",
  "common.export.resource": "Ressource",
  "common.export.health": "Zustand",
  "common.export.goesTo": "Führt zu",
  "common.export.checkedAt": "Geprüft",
  "common.export.times": "Mal gesehen",
  "common.export.exampleSite": "Beispielstandort",
  "common.export.noLimit": "Kein Limit",
  "common.export.lastSeen": "Zuletzt gesehen",
  "common.export.simSlot": "SIM-Steckplatz",
  "common.export.pinTries": "Verbleibende PIN-Versuche",
  "common.export.pukTries": "Verbleibende PUK-Versuche",
  "common.export.pinUsed": "Verwendete PIN",
  "common.export.storedPin": "Gespeicherte PIN des Routers stimmt",
  "common.export.readAt": "Gelesen am",
  "common.export.sims": "SIM-Steckplätze",
  "common.export.latency": "Latenz (ms)",
  "common.export.loss": "Verlust (%)",
  "common.export.peer": "Gegenstelle",
  "common.export.signal": "{name} (dBm)",
}

export default common
